/**
 * tRPC Provider Configuration
 *
 * This provider wraps the application with tRPC context, enabling type-safe
 * API calls using React Query. It shares the existing QueryClient instance
 * to allow tRPC queries to participate in the same cache.
 *
 * The provider configures:
 * - tRPC client with custom fetch for Amplify IAM signing
 * - Connection to existing React Query infrastructure
 * - API URL from environment or bootstrap config
 */

import { useState, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/config/queryClient'
import { trpc, createTRPCClient } from '@/api/trpc'

interface TRPCProviderProps {
  children: ReactNode
}

/**
 * Get the tRPC API URL from environment
 *
 * Priority:
 * 1. VITE_TRPC_API_URL environment variable
 * 2. Default empty string (Amplify will use configured endpoint)
 *
 * Note: The actual API endpoint is configured in Amplify via the tenant
 * bootstrap process. The URL here is used as the base for httpLink.
 */
function getTrpcApiUrl(): string {
  return import.meta.env.VITE_TRPC_API_URL || ''
}

/**
 * TRPCProvider wraps the app with both QueryClient and tRPC providers
 *
 * This component:
 * - Creates a tRPC client instance with the API URL
 * - Provides the tRPC context to all child components
 * - Integrates with the existing QueryClientProvider
 *
 * @example
 * ```tsx
 * <TRPCProvider>
 *   <App />
 * </TRPCProvider>
 * ```
 */
export function TRPCProvider({ children }: TRPCProviderProps): JSX.Element {
  // Create tRPC client once and memoize it
  const [trpcClient] = useState(() => createTRPCClient(getTrpcApiUrl()))

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </trpc.Provider>
  )
}

export default TRPCProvider
